import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createTranslator,
  DEFAULT_LOCALE,
  LOCALE_NAMES,
  negotiateLocale,
  SUPPORTED_LOCALES,
  type Catalog,
  type SupportedLocale,
  type Translator,
} from '@mailbutler/i18n';
import en from '@mailbutler/i18n/locales/en.json';
import nl from '@mailbutler/i18n/locales/nl.json';

/**
 * Catalogues are bundled rather than fetched: the shell must render correct text on first paint,
 * and both catalogues together are a few kilobytes. When the catalogue set grows past a handful
 * of languages this becomes a dynamic import keyed by locale — the interface below does not change.
 */
const CATALOGS: Record<SupportedLocale, Catalog> = {
  en: en,
  nl: nl,
};

const STORAGE_KEY = 'mailbutler.locale';

interface I18nContextValue {
  readonly locale: SupportedLocale;
  readonly setLocale: (locale: SupportedLocale) => void;
  readonly t: Translator['t'];
  readonly translator: Translator;
  readonly availableLocales: readonly SupportedLocale[];
  readonly localeNames: Record<SupportedLocale, string>;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function isSupported(value: string | null): value is SupportedLocale {
  return value !== null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function initialLocale(): SupportedLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // Private browsing or blocked storage: fall through to language negotiation.
  }

  const preferred = negotiateLocale(
    navigator.languages.join(','),
    SUPPORTED_LOCALES,
    DEFAULT_LOCALE,
  );
  return isSupported(preferred) ? preferred : DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale);

  useEffect(() => {
    // Keeps assistive technology, spell-checking and CSS :lang() rules in sync with the UI.
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: SupportedLocale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A user who cannot persist the choice still gets it for this session.
    }
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const translator = createTranslator({
      locale,
      catalog: CATALOGS[locale],
      fallbackCatalog: CATALOGS[DEFAULT_LOCALE],
      onMissing: (key) => {
        if (import.meta.env.DEV) {
          console.warn(`[i18n] missing translation "${key}" for locale "${locale}"`);
        }
      },
    });

    return {
      locale,
      setLocale,
      t: translator.t.bind(translator),
      translator,
      availableLocales: SUPPORTED_LOCALES,
      localeNames: LOCALE_NAMES,
    };
  }, [locale, setLocale]);

  return <I18nContext value={value}>{children}</I18nContext>;
}

export function useI18n(): I18nContextValue {
  const value = use(I18nContext);
  if (value === null) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return value;
}
